/* eslint-disable react/display-name */
import { useState } from "react";
import { createFragmentContainer, graphql } from "react-relay";

import Badge from "~/components/shared/Badge";
import Button from "~/components/shared/Button";
import Emojify from "~/components/shared/Emojify";
import Panel from "~/components/shared/Panel";
import UserAvatar from "~/components/shared/UserAvatar";

import permissions from "~/lib/permissions";
import FlashesStore from "~/stores/FlashesStore";

import OrganizationMemberSSOModeConstants, {
  OrganizationMemberSSOModes,
} from "~/constants/OrganizationMemberSSOModeConstants";

import Role from "~/components/Teams/Members/role";

const AVATAR_SIZE = 40;

type TeamMember = {
  user: any;
  role: string;
  permissions: {
    teamMemberUpdate:
      | {
          allowed: boolean;
        }
      | null
      | undefined;
    teamMemberDelete:
      | {
          allowed: boolean;
        }
      | null
      | undefined;
  };
  team: {
    organization: {
      slug: string;
    };
  };
  organizationMember: {
    uuid: string;
    security?: {
      twoFactorEnabled: boolean;
    };
    sso?: {
      mode: OrganizationMemberSSOModes;
    };
  };
};
type Props = {
  teamMember: TeamMember;
  onRemoveClick: (
    member: TeamMember,
    callback: (error?: string | null | undefined) => void,
  ) => void;
  onRoleChange: (
    member: TeamMember,
    role: string,
    callback: (error?: string | null | undefined) => void,
  ) => void;
};

const Row = ({ teamMember, onRemoveClick, onRoleChange }: Props) => {
  const [removing, setRemoving] = useState(false);
  const [savingNewRole, setSavingNewRole] = useState(null);

  const renderLabels = () => {
    const nodes: Array<React.ReactElement<React.ComponentProps<"div">>> = [];

    if (teamMember.user.bot) {
      nodes.push(
        <div key="bot" className="ml-1 flex">
          <Badge outline={true} className="font-normal">
            Bot <Emojify text=":robot_face:" />
          </Badge>
        </div>,
      );
    }

    if (
      teamMember.organizationMember.sso &&
      teamMember.organizationMember.sso.mode === OrganizationMemberSSOModeConstants.OPTIONAL
    ) {
      nodes.push(
        <div key="sso" className="ml-1 flex">
          <Badge outline={true} className="font-normal">
            SSO Optional
          </Badge>
        </div>,
      );
    }

    if (
      teamMember.organizationMember.security &&
      teamMember.organizationMember.security.twoFactorEnabled
    ) {
      nodes.push(
        <div key="2fa" className="ml-1 flex">
          <Badge
            title="Two-Factor authentication is configured."
            outline={true}
            className="border-lime lime"
          >
            2FA
          </Badge>
        </div>,
      );
    }

    return nodes;
  };

  const renderActions = () =>
    permissions(teamMember.permissions).collect(
      {
        always: true,
        render: (idx) => (
          <Role
            key={idx}
            teamMember={teamMember}
            onRoleChange={handleRoleChange}
            savingNewRole={savingNewRole}
            teamMemberUpdatePermission={
              teamMember.permissions && teamMember.permissions.teamMemberUpdate
            }
          />
        ),
      },
      {
        allowed: "teamMemberDelete",
        render: (idx) => (
          <Button
            key={idx}
            loading={removing ? "Removing…" : false}
            className="ml-3"
            onClick={handleMemberRemove}
          >
            Remove
          </Button>
        ),
      },
    );

  const handleRoleChange = (role: any) => {
    setSavingNewRole(role);

    onRoleChange(teamMember, role, (error) => {
      setSavingNewRole(null);
      if (error) {
        // @ts-expect-error - TS2339 - Property 'ERROR' does not exist on type 'FlashesStore'.
        FlashesStore.flash(FlashesStore.ERROR, error);
      }
    });
  };

  const handleMemberRemove = (evt: any) => {
    if (confirm("Remove this user from the team?")) {
      evt.preventDefault();
      setRemoving(true);

      onRemoveClick(teamMember, (error) => {
        setRemoving(false);

        if (error) {
          // @ts-expect-error - TS2339 - Property 'ERROR' does not exist on type 'FlashesStore'.
          FlashesStore.flash(FlashesStore.ERROR, error);
        }
      });
    }
  };

  return (
    <Panel.Row>
      <div className="flex">
        <a
          className="semi-bold black hover-lime hover-color-inherit-parent max-w-full truncate no-underline"
          href={`/organizations/${teamMember.team.organization.slug}/users/${teamMember.organizationMember.uuid}`}
        >
          <div className="flex-stretch flex items-center">
            <div className="mr-2 flex flex-none">
              <UserAvatar
                user={teamMember.user}
                className="align-middle"
                style={{ width: AVATAR_SIZE, height: AVATAR_SIZE }}
                suppressAltText={true}
              />
            </div>
            <div className="min-w-0 flex-auto">
              <div className="semi-bold m-0 flex items-center">
                {teamMember.user ? teamMember.user.name : ""}
                {renderLabels()}
              </div>
              {teamMember.user ? (
                <div className="mt-1 text-xs font-normal">{teamMember.user.email}</div>
              ) : (
                ""
              )}
            </div>
          </div>
        </a>
      </div>
      <Panel.RowActions className="ml-2">{renderActions()}</Panel.RowActions>
    </Panel.Row>
  );
};

export default createFragmentContainer(Row, {
  teamMember: graphql`
    fragment row_teamMember on TeamMember
    @argumentDefinitions(includeSensitiveInfo: { type: "Boolean!" }) {
      id
      user {
        name
        email
        avatar {
          url
        }
        bot
      }
      role
      team {
        organization {
          slug
        }
      }
      permissions {
        teamMemberUpdate {
          allowed
        }
        teamMemberDelete {
          allowed
        }
      }
      organizationMember {
        uuid
        sso @include(if: $includeSensitiveInfo) {
          mode
        }
        security @include(if: $includeSensitiveInfo) {
          twoFactorEnabled
        }
      }
    }
  `,
});
